import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { cartsService } from 'services/firestore';
import logger from 'utils/logger';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const syncTimeoutRef = useRef(null);
  const previousUserIdRef = useRef(null);

  // FEAT-04.4 — applied discount state.
  // Shape (or null when nothing's applied):
  //   { id, code, type, value, applies_to, applies_to_ids[], discountAmount }
  // discountAmount is the server-computed ₦ off the subtotal as of the last
  // validate call. It IS authoritative for what's shown to the customer, but
  // the order-create endpoint re-verifies and may compute a different amount
  // if the cart changed between apply and checkout.
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Load cart on mount - check if user is authenticated
  useEffect(() => {
    const loadCart = async () => {
      try {
        setIsLoading(true);

        if (currentUser) {
          // Load from Firestore for authenticated users
          const firestoreCart = await cartsService.getUserCart(currentUser.uid);
          setCart(firestoreCart);
        } else {
          // Load from localStorage for guests
          const savedCart = localStorage.getItem('curatedLagosCart');
          if (savedCart) {
            try {
              const parsedCart = JSON.parse(savedCart);
              if (Array.isArray(parsedCart)) {
                setCart(parsedCart);
              } else {
                logger.warn('Invalid cart format in localStorage, resetting');
                localStorage.removeItem('curatedLagosCart');
              }
            } catch (parseError) {
              logger.error('Error parsing cart from localStorage:', parseError);
              localStorage.removeItem('curatedLagosCart');
            }
          }
        }
      } catch (error) {
        logger.error('Error loading cart:', error);
        // Fallback to localStorage on error
        const savedCart = localStorage.getItem('curatedLagosCart');
        if (savedCart) {
          try {
            const parsedCart = JSON.parse(savedCart);
            if (Array.isArray(parsedCart)) {
              setCart(parsedCart);
            }
          } catch (parseError) {
            logger.error('Error parsing fallback cart:', parseError);
            localStorage.removeItem('curatedLagosCart');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadCart();
  }, []); // Only run on mount

  // Handle login - merge guest cart with user cart
  useEffect(() => {
    const handleLogin = async () => {
      // Check if user just logged in (previousUserId was null, now it's not)
      if (currentUser && !previousUserIdRef.current) {
        try {
          // Get guest cart from localStorage
          const guestCartStr = localStorage.getItem('curatedLagosCart');
          let guestCart = [];
          if (guestCartStr) {
            try {
              const parsed = JSON.parse(guestCartStr);
              guestCart = Array.isArray(parsed) ? parsed : [];
            } catch (parseError) {
              logger.error('Error parsing guest cart:', parseError);
              localStorage.removeItem('curatedLagosCart');
            }
          }

          if (guestCart.length > 0) {
            // Merge guest cart with user cart
            const result = await cartsService.mergeGuestCart(currentUser.uid, guestCart);
            if (result.success) {
              setCart(result.mergedCart);
              // Clear guest cart from localStorage
              localStorage.removeItem('curatedLagosCart');
            }
          } else {
            // No guest cart, just load user cart
            const userCart = await cartsService.getUserCart(currentUser.uid);
            setCart(userCart);
          }
        } catch (error) {
          logger.error('Error handling login cart merge:', error);
        }
      }

      // Update previous user ID ref
      previousUserIdRef.current = currentUser?.uid || null;
    };

    handleLogin();
  }, [currentUser?.uid]);

  // Sync cart to appropriate storage whenever it changes (debounced)
  useEffect(() => {
    if (isLoading) return; // Don't sync during initial load

    const syncCart = async () => {
      try {
        if (currentUser) {
          // Sync to Firestore for authenticated users
          setSyncInProgress(true);
          await cartsService.saveUserCart(currentUser.uid, cart);
        } else {
          // Save to localStorage for guests
          localStorage.setItem('curatedLagosCart', JSON.stringify(cart));
        }
      } catch (error) {
        logger.error('Error syncing cart:', error);
        // Always save to localStorage as backup
        localStorage.setItem('curatedLagosCart', JSON.stringify(cart));
      } finally {
        setSyncInProgress(false);
      }
    };

    // Debounce Firestore writes (300ms)
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(syncCart, 300);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [cart, currentUser, isLoading]);

  const addItem = useCallback((product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);

      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? {
                ...item,
                quantity: item.quantity + (product.quantity || 1),
                addons: product.addons && product.addons.length > 0
                  ? [...(item.addons || []), ...product.addons.filter(a =>
                      !item.addons?.some(existing => existing.id === a.id)
                    )]
                  : item.addons || [],
                message: product.message || item.message
              }
            : item
        );
      } else {
        return [...prevCart, {
          id: product.id,
          title: product.title,
          price: product.price,
          image: product.image,
          quantity: product.quantity || 1,
          addons: product.addons || [],
          message: product.message || ''
        }];
      }
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setCart(prevCart => prevCart.filter(item => item.id !== productId));
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const clearCart = useCallback(async () => {
    setCart([]);
    setAppliedDiscount(null); // FEAT-04.4 — clearing cart clears any applied code

    // Also clear from Firestore if user is authenticated
    if (currentUser) {
      try {
        await cartsService.clearUserCart(currentUser.uid);
      } catch (error) {
        logger.error('Error clearing cart from Firestore:', error);
      }
    }

    // Clear from localStorage
    localStorage.removeItem('curatedLagosCart');
  }, [currentUser]);

  // FEAT-04.4 — clear applied discount any time the cart changes underneath
  // it. Adding / removing / updating an item can change whether the discount
  // is still valid (min subtotal, min items, applies_to scope). Forcing the
  // operator to re-apply is the simplest correct behaviour for v1.
  const cartChangeKey = useMemo(
    () => cart.map((it) => `${it.id}:${it.quantity}`).join('|'),
    [cart]
  );
  useEffect(() => {
    setAppliedDiscount((prev) => (prev ? null : prev));
  }, [cartChangeKey]);

  const getTotalItems = useCallback(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const getSubtotal = useCallback(() => {
    return cart.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      const addonsTotal = (item.addons || []).reduce((sum, addon) => sum + addon.price, 0) * item.quantity;
      return total + itemTotal + addonsTotal;
    }, 0);
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getTotalItems,
    getSubtotal,
    isLoading,
    syncInProgress,
    // FEAT-04.4 — discount state. Checkout reads + writes via setAppliedDiscount.
    appliedDiscount,
    setAppliedDiscount,
  }), [cart, addItem, removeItem, updateQuantity, clearCart, getTotalItems, getSubtotal, isLoading, syncInProgress, appliedDiscount]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

